import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { canvaApi } from '@/lib/api';
import { useAppStore } from '@/stores/appStore';
import { ExternalLink, Unlink, Check } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';

export function SettingsCanva() {
  const { getToken } = useAuth();
  const { currentAgency } = useAppStore();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const [connectError, setConnectError] = useState<string | null>(null);

  useEffect(() => {
    if (searchParams.get('connected') === 'true') {
      qc.invalidateQueries({ queryKey: ['canva-status', currentAgency?.id] });
    }
  }, [searchParams]);

  const { data: statusData } = useQuery({
    queryKey: ['canva-status', currentAgency?.id],
    queryFn: async () => {
      const token = await getToken();
      return canvaApi.status(token!, currentAgency!.id);
    },
    enabled: !!currentAgency,
  });

  const connect = async () => {
    if (!currentAgency) return;
    setConnectError(null);
    try {
      const token = await getToken();
      const { authUrl } = await canvaApi.getOAuthUrl(token!, currentAgency.id);
      window.location.href = authUrl;
    } catch (e) {
      // e.g. 503 "Canva integration not configured" when the OAuth secrets are unset
      setConnectError(e instanceof Error ? e.message : 'Failed to start the Canva connection');
    }
  };

  const disconnect = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      return canvaApi.disconnect(token!, currentAgency!.id);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['canva-status', currentAgency?.id] }),
  });

  if (!currentAgency) {
    return <p className="text-sm text-muted-foreground">No agency selected</p>;
  }

  const connected = statusData?.connected;

  return (
    <div className="max-w-lg space-y-6">
      <div>
        <h2 className="text-base font-semibold text-foreground mb-1">Canva Connect</h2>
        <p className="text-xs text-muted-foreground">
          Connect Canva to access your designs and export them directly to your media library
        </p>
      </div>

      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-[#7D2AE8] flex items-center justify-center">
              <span className="text-white text-sm font-bold">C</span>
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">Canva</p>
              <p className="text-xs text-muted-foreground">
                {connected ? 'Connected' : 'Not connected'}
              </p>
            </div>
          </div>
          {connected ? (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 text-success text-xs">
                <Check className="h-3.5 w-3.5" />
                Connected
              </div>
              <button
                onClick={() => disconnect.mutate()}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-destructive bg-destructive/10 rounded-md hover:bg-destructive/20"
              >
                <Unlink className="h-3.5 w-3.5" />
                Disconnect
              </button>
            </div>
          ) : (
            <button
              onClick={connect}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#7D2AE8] text-white rounded-md hover:bg-[#7D2AE8]/90"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Connect Canva
            </button>
          )}
        </div>
        {connectError && !connected && (
          <p className="mt-3 text-xs text-destructive">{connectError}</p>
        )}
      </div>

      {connected && (
        <div className="bg-card border border-border rounded-lg p-4 space-y-3">
          <h3 className="text-xs font-medium text-foreground">Features</h3>
          <ul className="space-y-2 text-xs text-muted-foreground">
            <li className="flex items-center gap-2">
              <Check className="h-3.5 w-3.5 text-success" />
              Browse and select Canva designs
            </li>
            <li className="flex items-center gap-2">
              <Check className="h-3.5 w-3.5 text-success" />
              Export designs to R2 media library (PNG, JPG, MP4)
            </li>
            <li className="flex items-center gap-2">
              <Check className="h-3.5 w-3.5 text-success" />
              Attach Canva designs directly to posts
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}
